/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
import { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, TextField, Box, InputAdornment, IconButton } from '@mui/material';
import { ConversationType, useOTJCommHelper } from 'otj-client-lib';

import Modal from '../../../Modules/Jobs/Utilities/Shared/Components/Modal';
import ConversationsApiService from './ConversationsService';
import { ISupportModalProps, ISupportOption } from './Conversations.model';
import { BILLING_USER_ID, GENERAL_SUPPORT_USER_ID, SUPPORT_TYPES, velocitiOrganizationId } from '../../Constants/App';
import MasterDataApiService from '../../Services/MasterDataService';
import SnackbarMessage from '../Common/SnackbarMessage/SnackbarMessage';
import Button, { ButtonType } from '../Common/Buttons/Button';
import { useJobsList } from './MessagesScreen/useJobsList';
import CustomDropDownAutoComplete from '../Common/CustomDropDown/CustomDropdownAutoComplete';

import { Icons } from '../../Constants/Icons';
import { Options } from '../Common/Dropdown/Dropdown.styles';
import { buttonStyles, HeaderText } from './DashboardConversations.Style';

const SupportTypeModal = ({
  open,
  onClose,
  loggedInUserInternalId,
  loggedInUserName,
  organizationName,
  customerId,
  onNewConversation,
}: ISupportModalProps): JSX.Element => {
  const { t } = useTranslation(['vendor']);

  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedJobId, setSelectedJobId] = useState<string>('');
  const [selectedJobName, setSelectedJobName] = useState<string>('');
  const [message, setMessage] = useState<string>('');
  const [isSupportTypeOpen, setIsSupportTypeOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [supportOptions, setSupportOptions] = useState<ISupportOption[]>([]);
  const [isFetchingOptions, setIsFetchingOptions] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const SUPPORT_TYPES_WITH_JOBS = [SUPPORT_TYPES.dispatch, SUPPORT_TYPES.accountManager];

  const { chatHooks } = useOTJCommHelper();
  const { sendMessage, createConversation } = chatHooks;
  const showJobsDropdown = SUPPORT_TYPES_WITH_JOBS.includes(selectedType);

  const fetchJobSupportContacts = async (
    jobId: string,
    orgId: string,
    userId: string
  ): Promise<{ accountManagerIds: string[]; districtManagerIds: string[] }> => {
    try {
      const response = await ConversationsApiService.fetchJobManagersEndPoint(jobId, orgId, userId);

      if (response?.errors?.length) {
        console.error('API Errors:', response.errors);
        return { accountManagerIds: [], districtManagerIds: [] };
      }

      const data = response?.data?.managers;
      return {
        accountManagerIds: data?.accountManager ?? [],
        districtManagerIds: data?.districtManager ?? [],
      };
    } catch (error) {
      console.error('Error fetching job support contacts:', error);
      return { accountManagerIds: [], districtManagerIds: [] };
    }
  };

  const resolveUserIds = async (
    supportType: string,
    loggedInUserInternalId: string,
    isVelocitiOrg: boolean,
    jobId?: string,
    orgId?: string
  ): Promise<string[]> => {
    if (!isVelocitiOrg) {
      return [loggedInUserInternalId, GENERAL_SUPPORT_USER_ID];
    }
    switch (supportType) {
      case SUPPORT_TYPES.dispatch: {
        const { accountManagerIds, districtManagerIds } = await fetchJobSupportContacts(
          jobId!,
          orgId!,
          loggedInUserInternalId
        );
        return [loggedInUserInternalId, ...accountManagerIds, ...districtManagerIds];
      }
      case SUPPORT_TYPES.accountManager: {
        const { accountManagerIds } = await fetchJobSupportContacts(jobId!, orgId!, loggedInUserInternalId);
        return [loggedInUserInternalId, ...accountManagerIds];
      }
      case SUPPORT_TYPES.billing:
        return [loggedInUserInternalId, BILLING_USER_ID];
      case SUPPORT_TYPES.generalSupport:
        return [loggedInUserInternalId, GENERAL_SUPPORT_USER_ID];
      default:
        return [loggedInUserInternalId];
    }
  };

  const {
    jobs,
    isLoading: isJobsLoading,
    totalCounts,
    currentPage,
    searchString,
    setSearchString,
    handlePaginationChanges,
    handleSearch,
  } = useJobsList(open && showJobsDropdown);

  const handleTypeChange = (value: string) => {
    setSelectedType(value);
    setSelectedJobId('');
    setSelectedJobName('');
  };

  const handleJobChange = (jobId: string) => {
    setSelectedJobId(jobId);
    const job = jobs.find((j) => j.jobId === jobId);
    setSelectedJobName(job?.jobName ?? '');
  };

  const handleClose = () => {
    setSelectedType('');
    setSelectedJobId('');
    setSelectedJobName('');
    setMessage('');
    setIsSupportTypeOpen(false);
    setErrorMsg('');
    onClose();
  };

  const handleSubmit = useCallback(async () => {
    if (!selectedType || !message.trim()) return;
    if (showJobsDropdown && !selectedJobId) return;

    try {
      setIsSubmitting(true);
      setErrorMsg('');
      const isVelocitiOrg = customerId === velocitiOrganizationId;
      const userIds = await resolveUserIds(
        selectedType,
        loggedInUserInternalId,
        isVelocitiOrg,
        showJobsDropdown ? selectedJobId : undefined,
        customerId
      );

      const conversationName = supportOptions?.find((o) => o?.value === selectedType)?.label ?? selectedType;

      const meta = {
        jobName: selectedJobName ?? '',
        isFromJob: showJobsDropdown,
        currentJobId: selectedJobId ?? '',
        supportType: selectedType,
        initialMessage: message,
      };

      const response = (await createConversation?.({
        userIds,
        adminIds: [loggedInUserInternalId],
        mode: 'GROUP',
        name: conversationName,
        meta,
        userData: [{ id: loggedInUserInternalId, fullName: loggedInUserName }],
      })) as ConversationType;

      if (response) {
        await sendMessage?.({
          text: message,
          conversationId: response.id,
          meta: {
            senderName: loggedInUserName ?? '',
            senderId: loggedInUserInternalId ?? '',
            senderCompanyName: organizationName ?? '',
          },
        });

        onNewConversation(response);
        handleClose();
      } else {
        setErrorMsg('Failed to create conversation. Please try again.');
      }
    } catch (e) {
      console.error('Error creating conversation:', e);
      handleClose();
      setSnackbarMessage(t('jobManagement:someThingWentWrong'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    selectedType,
    selectedJobId,
    selectedJobName,
    message,
    showJobsDropdown,
    loggedInUserName,
    loggedInUserInternalId,
    organizationName,
    supportOptions,
    sendMessage,
  ]);

  const dropdownAdornment = (isOpen: boolean, setOpen: (v: boolean) => void) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton onClick={() => setOpen(!isOpen)} style={{ padding: 0 }}>
          <img src={isOpen ? Icons.DropdownDownIcon : Icons.DropdownUpIcon} alt={isOpen ? 'up' : 'down'} />
        </IconButton>
      </InputAdornment>
    ),
  });

  const dropdownSelectProps = (isOpen: boolean, setOpen: (v: boolean) => void) => ({
    open: isOpen,
    IconComponent: () => null,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
    MenuProps: { PaperProps: {} },
  });

  const isSubmitDisabled = !selectedType || (showJobsDropdown && !selectedJobId) || !message.trim() || isSubmitting;

  const renderHeader = <HeaderText>{t('conversations:customerSupport')}</HeaderText>;

  const renderBody = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', px: 1, pt: 0.5 }}>
      <TextField
        select
        sx={{ ...buttonStyles.textField }}
        label={t('conversations:supportCategory')}
        value={selectedType}
        onChange={(e) => handleTypeChange(e.target.value)}
        InputProps={dropdownAdornment(isSupportTypeOpen, setIsSupportTypeOpen)}
        SelectProps={dropdownSelectProps(isSupportTypeOpen, setIsSupportTypeOpen)}
        disabled={isFetchingOptions}>
        {supportOptions?.map((opt) => (
          <Options key={opt?.value} value={opt?.value}>
            {opt?.label}
          </Options>
        ))}
      </TextField>
      {showJobsDropdown && (
        <Box sx={{ width: '100%', '& > div': { gap: 0 } }}>
          <CustomDropDownAutoComplete
            options={jobs.map((job) => ({ label: job.jobName, value: job.jobId }))}
            selectedValue={selectedJobId}
            onChange={(e: any) => handleJobChange(e?.target?.value ?? '')}
            isLoading={isJobsLoading}
            label={t('conversations:selectJob')}
            totalCounts={totalCounts}
            handlePaginationChanges={handlePaginationChanges}
            currentPages={currentPage}
            searchString={searchString}
            setSearchString={setSearchString}
            onSearch={handleSearch}
            isInline={false}
            isDisabled={false}
            dropDownWidth="100%"
            sx={buttonStyles.textField}
          />
        </Box>
      )}

      <TextField
        label={t('vendor:message')}
        placeholder={t('conversations:TypeMessageHere')}
        multiline
        fullWidth
        sx={{ ...buttonStyles.textField }}
        value={message}
        onChange={(e) => setMessage(e.target.value)}
      />

      {errorMsg && (
        <Typography color="error" variant="caption">
          {errorMsg}
        </Typography>
      )}

      <Button
        label={t('conversations:sendMessage')}
        onClick={handleSubmit}
        type={ButtonType.Primary}
        disabled={isSubmitDisabled}
        isLoading={isSubmitting}
      />
    </Box>
  );

  useEffect(() => {
    if (!open) return;
    const fetchSupportCategories = async () => {
      setIsFetchingOptions(true);
      try {
        const response = await MasterDataApiService.getMasterDataCategories('SUPPORT_CATEGORY');

        if (response?.errors?.length) {
          console.error('Error fetching support categories:', response?.errors);
          return;
        }
        const options: ISupportOption[] = (response?.data ?? []).map((item: any) => ({
          label: item?.subKey1Value,
          value: item?.value,
        }));
        setSupportOptions(options);
      } catch (error) {
        console.error('Failed to fetch support categories:', error);
      } finally {
        setIsFetchingOptions(false);
      }
    };
    fetchSupportCategories();
  }, [open]);

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        renderHeader={renderHeader}
        renderBody={renderBody}
        styles={{ '& .MuiPaper-root': { width: '40%' } }}
      />
      <SnackbarMessage
        open={snackbarOpen}
        successMessage={snackbarMessage}
        errorMessage={snackbarMessage}
        severity={snackbarSeverity}
        onClose={() => setSnackbarOpen(false)}
      />
    </>
  );
};

export default SupportTypeModal;
